import React, { useEffect, useRef, useState } from "react";

/* ══════════════════════════════════════════════════════════════════════
   PROCUREX — THE COMMERCIAL THREAD  (v2 — aligned layout)
   Canvas: 680×440. Thread now dominates the visual center.
   Every event anchored to the live thread curve via threadY(x) helper.
   Labels distributed onto 5 non-colliding y-tracks.
   ══════════════════════════════════════════════════════════════════════ */

const W = 680, H = 440;

/* Thread control points — moved up from v1 and tightened */
const THREAD_ANCHORS = [
    [0, 260], [60, 252], [120, 238], [180, 222],
    [230, 210], [300, 198], [380, 190],
    [450, 185], [520, 180], [612, 174],
    [650, 171], [670, 169], [680, 168],
];

const THREAD_D = "M 0 260 C 60 252, 120 238, 180 222 C 230 210, 300 198, 380 190 C 450 185, 520 180, 612 174 C 650 171, 670 169, 680 168";

/* Helper: approximate thread y at any x via linear interpolation */
function threadY(x) {
    const pts = THREAD_ANCHORS;
    if (x <= pts[0][0]) return pts[0][1];
    if (x >= pts[pts.length - 1][0]) return pts[pts.length - 1][1];
    for (let i = 0; i < pts.length - 1; i++) {
        if (x >= pts[i][0] && x <= pts[i + 1][0]) {
            const [x1, y1] = pts[i];
            const [x2, y2] = pts[i + 1];
            const t = (x - x1) / (x2 - x1);
            return y1 + (y2 - y1) * t;
        }
    }
    return pts[pts.length - 1][1];
}

/* Label y-tracks (stable horizontal rails) */
const TRACK_CLAIM_ABOVE = 110; // claim "!" labels at top of canvas
const TRACK_CLAUSE_UP   = 148; // up-pointing clause labels
const TRACK_RISK        = 125; // risk gauge
const TRACK_CLAUSE_DOWN = 235; // down-pointing clause labels
const TRACK_MILESTONE   = 225; // milestone labels
const TRACK_VO          = 255; // variation-order labels
const TRACK_LD          = 275; // LD accrual label
const TRACK_AWARDED     = 255; // awarded label (sourcing zone; won't collide with exec)
const TRACK_CATEGORY    = 305; // category labels BELOW thread
const TRACK_FINAL       = 210; // final-account label

const ZONES = [
    { id: "cat",  label: "CATEGORY",    sub: "Kraljic",             x0: 0,   x1: 82  },
    { id: "sup",  label: "SUPPLIER",    sub: "Qualify · 360°",      x0: 82,  x1: 176 },
    { id: "src",  label: "SOURCING",    sub: "eAuction · BAFO",     x0: 176, x1: 272 },
    { id: "con",  label: "CONTRACT",    sub: "FIDIC · clause risk", x0: 272, x1: 380 },
    { id: "exe",  label: "EXECUTION",   sub: "Claims · LD · VO",    x0: 380, x1: 612 },
    { id: "set",  label: "SETTLEMENT",  sub: "Close-out",           x0: 612, x1: 680 },
];

/* Category nodes: spaced in Zone 1 (x=0-82) */
const CATEGORIES = [
    { x: 18, tag: "HVY EQ",   kraljic: "strategic" },
    { x: 40, tag: "EPC",      kraljic: "leverage" },
    { x: 64, tag: "SPEC ENG", kraljic: "bottleneck" },
];

/* Supplier sub-strands — 5 candidates, keep the top 3 */
const SUPPLIERS = [
    { code: "V-01", score: 87, fanY: -22, keep: true  },
    { code: "V-02", score: 76, fanY: -10, keep: true  },
    { code: "V-03", score: 69, fanY: 2,   keep: true  },
    { code: "V-04", score: 54, fanY: 14,  keep: false },
    { code: "V-05", score: 43, fanY: 26,  keep: false },
];

/* Sourcing bids — 4 parallel, 1 winner */
const BIDS = [
    { price: "₹84 Cr", fanY: -16, winner: false },
    { price: "₹79 Cr", fanY: -5,  winner: false },
    { price: "₹76 Cr", fanY: 7,   winner: true  },
    { price: "₹91 Cr", fanY: 19,  winner: false },
];

/* Contract clauses — alternate above/below */
const CLAUSES = [
    { x: 282, label: "§ 8.4 EOT",    risk: "std",    dir: "up"   },
    { x: 312, label: "§ 20.2 NOTICE",risk: "std",    dir: "down" },
    { x: 342, label: "§ 14.7 LD",    risk: "garnet", dir: "up"   },
    { x: 370, label: "§ 17 INDEM",   risk: "amber",  dir: "down" },
];

/* Execution zone — well-separated x positions */
const MILESTONES = [
    { x: 410, id: "M1", name: "FEED"      },
    { x: 448, id: "M2", name: "DELIVERY"  },
    { x: 494, id: "M3", name: "MECH"      },
    { x: 545, id: "M4", name: "COMMISS"   },
    { x: 593, id: "M5", name: "PERF TEST" },
];

const VARIATIONS = [
    { x0: 415, x1: 445, label: "VO-007 · ₹2.4 Cr" },
    { x0: 560, x1: 590, label: "VO-012 · 14D"     },
];

const CLAIMS = [
    { x: 472, label: "₹47 Cr · EOT",  track: 108 },
    { x: 570, label: "₹12 Cr · DISR", track: 130 },
];

const LD = { x0: 470, x1: 520, label: "LD ₹14 Cr TOTAL" };

const EVIDENCE_POOL = [
    { t: 0.08, card: { head: "KRALJIC REFRESH",     body: "Heavy equipment: leverage → strategic",    sub: "3 primary + 2 backup suppliers" } },
    { t: 0.20, card: { head: "QUALIFICATION COMPLETE", body: "3 of 5 candidates qualified",            sub: "Score threshold: 65 · FM validated" } },
    { t: 0.33, card: { head: "REVERSE AUCTION DONE",   body: "Final bid ₹76 Cr · 8.4% savings",         sub: "Awarded to V-03 (Score 87)" } },
    { t: 0.48, card: { head: "CLAUSE RISK ANALYSIS",   body: "§ 14.7 LD cap · 10% of contract",         sub: "Remove 'subject to actual loss'" } },
    { t: 0.62, card: { head: "MILESTONE ACHIEVED",     body: "M2 First delivery · on schedule",         sub: "Payment ₹18 Cr released" } },
    { t: 0.72, card: { head: "CLAIM ANALYSIS",         body: "Claim ₹47 Cr · EOT under FIDIC § 8.4",    sub: "Evidence support: 94% · defensible" } },
    { t: 0.82, card: { head: "LD ACCRUAL ALERT",       body: "M3 slipped 28 days",                      sub: "LD accruing ₹0.5 Cr/day · ₹14 Cr" } },
    { t: 0.95, card: { head: "FINAL ACCOUNT",          body: "Contract closed · ₹94 Cr final value",    sub: "All claims settled · 97% of scope" } },
];

const CYCLE_MS = 45000;

/* ══════════════════════════════════════════════════════════════════════
   COMPONENT
   ══════════════════════════════════════════════════════════════════════ */
export default function ProcureXCommercialThread() {
    const [reduceMotion, setReduceMotion] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        setReduceMotion(mq.matches);
        const fn = (e) => setReduceMotion(e.matches);
        mq.addEventListener("change", fn);
        return () => mq.removeEventListener("change", fn);
    }, []);

    const pathRef = useRef(null);
    const [progress, setProgress] = useState(reduceMotion ? 0.62 : 0);
    const [playheadPt, setPlayheadPt] = useState({ x: 0, y: 260 });
    const startRef = useRef(performance.now());
    const rafRef = useRef(null);

    useEffect(() => {
        if (reduceMotion) { setProgress(0.62); return; }
        const step = (now) => {
            const raw = ((now - startRef.current) % CYCLE_MS) / CYCLE_MS;
            let p = raw;
            EVIDENCE_POOL.forEach((e) => {
                const d = raw - e.t;
                if (d > 0 && d < 0.04) p = e.t + d * 0.15;
            });
            setProgress(p);
            rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, [reduceMotion]);

    useEffect(() => {
        if (!pathRef.current) return;
        const len = pathRef.current.getTotalLength();
        const pt = pathRef.current.getPointAtLength(progress * len);
        setPlayheadPt({ x: pt.x, y: pt.y });
    }, [progress]);

    const activeEvidence = EVIDENCE_POOL.find((e) => Math.abs(progress - e.t) < 0.035);
    const activeZone = ZONES.find((z) => playheadPt.x >= z.x0 && playheadPt.x < z.x1) || ZONES[ZONES.length - 1];

    /* Evidence card position — above the thread since thread is now upper-mid */
    const cardW = 220, cardH = 50;
    let cardX = Math.max(12, Math.min(W - cardW - 12, playheadPt.x - cardW / 2));
    let cardY = Math.max(80, playheadPt.y - 62);

    return (
        <div className="sg-wrap" data-testid="sg-canvas" role="img"
             aria-label="Animated illustration: ProcureX as the commercial thread of a capital project.">
            <svg viewBox={`0 0 ${W} ${H}`} className={`sg-svg ${reduceMotion ? "sg-reduced" : ""}`}
                 preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                <defs>
                    <pattern id="sg-grid" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
                        <path d="M 32 0 L 0 0 0 32" fill="none" className="sg-grid-line" />
                    </pattern>
                    <filter id="sg-thread-glow" x="-20%" y="-200%" width="140%" height="500%">
                        <feGaussianBlur stdDeviation="2.5" result="blur" />
                        <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                </defs>

                <rect x="0" y="26" width={W} height={H - 48} fill="url(#sg-grid)" />

                {/* Zone bands */}
                {ZONES.map((z, i) => (
                    <g key={z.id} className={`sg-zone ${activeZone.id === z.id ? "sg-zone-active" : ""}`}>
                        {i > 0 && <line x1={z.x0} y1="30" x2={z.x0} y2={H - 24} className="sg-zone-divider" />}
                        <rect x={z.x0} y="30" width={z.x1 - z.x0} height={H - 54} className="sg-zone-bg" />
                        <text x={(z.x0 + z.x1) / 2} y="48" textAnchor="middle" className="sg-zone-label">{z.label}</text>
                        <text x={(z.x0 + z.x1) / 2} y="62" textAnchor="middle" className="sg-zone-sub">{z.sub}</text>
                    </g>
                ))}

                {/* Supplier sub-strands — anchored to thread at entry/exit x */}
                <g className="sg-suppliers">
                    {SUPPLIERS.map((s, i) => {
                        const inX = 95, midX = 129, outX = 165;
                        const inY = threadY(inX), outY = threadY(outX);
                        const midY = threadY(midX) + s.fanY;
                        const path = `M ${inX} ${inY} Q ${midX} ${midY} ${outX} ${outY}`;
                        return (
                            <g key={s.code} className={s.keep ? "sg-sup-keep" : "sg-sup-drop"}>
                                <path d={path} className="sg-sup-strand" />
                                <circle cx={midX} cy={midY} r="6" className="sg-sup-score" />
                                <text x={midX} y={midY + 2.2} textAnchor="middle" className="sg-sup-score-txt">{s.score}</text>
                            </g>
                        );
                    })}
                </g>

                {/* Sourcing bid strands */}
                <g className="sg-bids">
                    {BIDS.map((b, i) => {
                        const inX = 190, midX = 220, outX = 252;
                        const inY = threadY(inX), outY = threadY(outX);
                        const midY = threadY(midX) + b.fanY;
                        const path = `M ${inX} ${inY} Q ${midX} ${midY} ${outX} ${outY}`;
                        return (
                            <g key={i} className={b.winner ? "sg-bid-win" : "sg-bid-lose"}>
                                <path d={path} className="sg-bid-strand" />
                                <g transform={`translate(${midX}, ${midY})`}>
                                    <rect x="-15" y="-6" width="30" height="11" rx="2" className="sg-bid-box" />
                                    <text x="0" y="3" textAnchor="middle" className="sg-bid-price">{b.price}</text>
                                </g>
                            </g>
                        );
                    })}
                    {/* Win marker — placed BELOW the thread in the AWARDED track */}
                    <g transform={`translate(258, ${TRACK_AWARDED})`}>
                        <path d="M -4 0 L -1 3 L 4 -3" className="sg-win-check" />
                        <text x="8" y="3" className="sg-win-label">AWARDED ₹76 Cr</text>
                    </g>
                </g>

                {/* Contract clause ticks — alternate up/down, labels on dedicated tracks */}
                <g className="sg-clauses">
                    {CLAUSES.map((c, i) => {
                        const tY = threadY(c.x);
                        const up = c.dir === "up";
                        const tickEndY = up ? tY - 14 : tY + 14;
                        const labelY = up ? TRACK_CLAUSE_UP : TRACK_CLAUSE_DOWN;
                        return (
                            <g key={i} className={`sg-clause sg-clause-${c.risk}`}>
                                <line x1={c.x} y1={tY} x2={c.x} y2={tickEndY} className="sg-clause-tick" />
                                <line x1={c.x} y1={tickEndY} x2={c.x} y2={labelY + (up ? 3 : -3)} className="sg-clause-leader" />
                                <text x={c.x} y={labelY} textAnchor="middle" className="sg-clause-label">{c.label}</text>
                            </g>
                        );
                    })}
                    {/* Risk gauge — above CONTRACT zone, centered */}
                    <g transform={`translate(326, ${TRACK_RISK})`}>
                        <path d="M -10 0 A 10 10 0 0 1 10 0" className="sg-risk-arc" />
                        <path d="M -10 0 A 10 10 0 0 1 4 -9" className="sg-risk-fill" />
                        <text x="0" y="-14" textAnchor="middle" className="sg-risk-label">RISK · MODERATE</text>
                    </g>
                </g>

                {/* Execution events */}
                <g className="sg-exec">
                    {/* Milestones */}
                    {MILESTONES.map((m) => {
                        const tY = threadY(m.x);
                        const reached = playheadPt.x >= m.x - 4;
                        return (
                            <g key={m.id} className={`sg-ms ${reached ? "sg-ms-reached" : ""}`}>
                                <line x1={m.x} y1={tY - 7} x2={m.x} y2={tY + 7} className="sg-ms-tick" />
                                <text x={m.x} y={TRACK_MILESTONE} textAnchor="middle" className="sg-ms-label">{m.id}</text>
                                {reached && (
                                    <g transform={`translate(${m.x + 3}, ${tY - 12})`}>
                                        <path d="M -3 0 L -1 2 L 3 -2" className="sg-ms-check" />
                                    </g>
                                )}
                            </g>
                        );
                    })}

                    {/* Variation branches — curve DOWNWARD below thread */}
                    {VARIATIONS.map((v, i) => {
                        const midX = (v.x0 + v.x1) / 2;
                        const y0 = threadY(v.x0), y1 = threadY(v.x1);
                        const bumpY = Math.max(y0, y1) + 14;
                        const path = `M ${v.x0} ${y0} Q ${midX} ${bumpY} ${v.x1} ${y1}`;
                        return (
                            <g key={i} className="sg-var">
                                <path d={path} className="sg-var-path" />
                                <text x={midX} y={TRACK_VO} textAnchor="middle" className="sg-var-label">{v.label}</text>
                            </g>
                        );
                    })}

                    {/* Claim tension points — bump ABOVE the thread, labels on staggered tracks */}
                    {CLAIMS.map((cl, i) => {
                        const tY = threadY(cl.x);
                        const bumpY = tY - 12;
                        return (
                            <g key={i} className="sg-claim">
                                <circle cx={cl.x} cy={bumpY} r="5" className="sg-claim-dot" />
                                <text x={cl.x} y={bumpY + 2.5} textAnchor="middle" className="sg-claim-bang">!</text>
                                <text x={cl.x} y={cl.track} textAnchor="middle" className="sg-claim-label">{cl.label}</text>
                                <line x1={cl.x} y1={bumpY - 7} x2={cl.x} y2={cl.track + 3} className="sg-claim-leader" />
                            </g>
                        );
                    })}

                    {/* LD accrual pinch — garnet band under thread */}
                    <g className="sg-ld">
                        <rect x={LD.x0} y={threadY((LD.x0 + LD.x1) / 2) + 4} width={LD.x1 - LD.x0} height="5" className="sg-ld-pinch" />
                        <text x={(LD.x0 + LD.x1) / 2} y={TRACK_LD} textAnchor="middle" className="sg-ld-label">{LD.label}</text>
                    </g>
                </g>

                {/* Category nodes — diamonds ON the thread, labels BELOW on dedicated track */}
                <g className="sg-cats">
                    {CATEGORIES.map((c, i) => {
                        const y = threadY(c.x);
                        return (
                            <g key={i} className={`sg-cat sg-cat-${c.kraljic}`} style={{ animationDelay: `${i * 2.5}s` }}>
                                <rect x={c.x - 4} y={y - 4} width="8" height="8"
                                      transform={`rotate(45 ${c.x} ${y})`} className="sg-cat-shape" />
                                <line x1={c.x} y1={y + 6} x2={c.x} y2={TRACK_CATEGORY - 8} className="sg-cat-leader" />
                                <text x={c.x} y={TRACK_CATEGORY} textAnchor="middle" className="sg-cat-label">{c.tag}</text>
                            </g>
                        );
                    })}
                    {/* Spend cube — in upper-left quadrant above CATEGORY zone */}
                    <g transform="translate(14, 100)" className="sg-cube">
                        <rect x="0" y="0" width="14" height="14" className="sg-cube-face" />
                        <rect x="4" y="-4" width="14" height="14" className="sg-cube-face" />
                        <rect x="8" y="-8" width="14" height="14" className="sg-cube-face" />
                    </g>
                </g>

                {/* THE THREAD */}
                <path ref={pathRef} d={THREAD_D} className="sg-thread-glow" filter="url(#sg-thread-glow)" />
                <path d={THREAD_D} className="sg-thread-main" />
                <path d={THREAD_D} className="sg-thread-shimmer" />

                {/* Final account marker */}
                <g transform={`translate(665, ${threadY(665)})`} className="sg-final">
                    <circle r="7" className="sg-final-circle" />
                    <path d="M -3 0 L -1 2 L 3 -2" className="sg-final-check" />
                    <text x="-12" y="26" textAnchor="end" className="sg-final-label">FINAL · ₹94 Cr</text>
                </g>

                {/* Playhead */}
                <g className="sg-playhead" transform={`translate(${playheadPt.x}, ${playheadPt.y})`}>
                    <circle r="9" className="sg-ph-glow" />
                    <circle r="5" className="sg-ph-core" />
                </g>

                {/* Evidence card */}
                {(activeEvidence || reduceMotion) && (
                    <g transform={`translate(${cardX}, ${cardY})`}>
                        <g className="sg-card">
                            <rect width={cardW} height={cardH} rx="6" className="sg-card-bg" />
                            <path d="M 10 12 L 7 17 L 10 17 L 9 22 L 14 15 L 11 15 Z" className="sg-card-spark" />
                            <text x="20" y="16" className="sg-card-head">
                                {(activeEvidence?.card.head) || "MILESTONE ACHIEVED"}
                            </text>
                            <text x="10" y="30" className="sg-card-body">
                                {(activeEvidence?.card.body) || "M3 Mech completion · on track"}
                            </text>
                            <text x="10" y="44" className="sg-card-sub">
                                {(activeEvidence?.card.sub) || "Payment ₹24 Cr released"}
                            </text>
                        </g>
                    </g>
                )}

                {/* Top overlay */}
                <rect x="0" y="0" width={W} height="24" className="sg-topbar" />
                <text x="8" y="16" className="sg-top-txt">CONTRACT · C-2024-087 · ₹84 CR</text>
                <text x={W / 2} y="16" textAnchor="middle" className="sg-top-txt">LIVE · COMMERCIAL LIFECYCLE</text>
                <g transform={`translate(${W - 8}, 12)`}>
                    <text x="0" y="4" textAnchor="end" className="sg-top-txt">12 MODULES · 5 PILLARS</text>
                    <path d="M-126,-4 L-130,1 L-126,1 L-127,6 L-122,-1 L-125,-1 Z" className="sg-spark sg-spark-static" />
                </g>
                <line x1="0" y1="25" x2={W} y2="25" className="sg-hair" />

                {/* Bottom overlay */}
                <line x1="0" y1={H - 18} x2={W} y2={H - 18} className="sg-hair" />
                <text x="8" y={H - 4} className="sg-bot-label">CATEGORIZE</text>
                <text x={W / 2} y={H - 4} textAnchor="middle" className="sg-bot-label sg-bot-accent">NEGOTIATE</text>
                <text x={W - 8} y={H - 4} textAnchor="end" className="sg-bot-label sg-bot-accent">SETTLE</text>
            </svg>

            <span className="sr-only">
                ProcureX shows the commercial lifecycle as a single continuous thread traversing six zones.
            </span>
        </div>
    );
}
